import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@core/services/api/auth.service';
import { ViewportService } from '@core/services/viewport.service';
import { rememberRedirectIntent } from '@shared/utils/redirect-intent';

/** Separate, tab-local slot — deliberately NOT the real `electa.jwt` slot. */
export const PREVIEW_TOKEN_STORAGE_KEY = 'electa.previewJwt';

const PREVIEW_SCOPE = 'TASK_PREVIEW';

interface PreviewJwtPayload {
  scope?: string;
  previewTaskId?: string;
  exp?: number;
}

export interface PreviewSessionResponse {
  jwt: string;
  taskId: string;
}

/**
 * Owns the read-only task-preview session. It lives completely beside the real
 * login: the preview JWT is kept in its own `sessionStorage` slot and a real
 * session always wins (`isActive()` is false while logged in). Nothing here
 * touches `AuthService` token storage, so bootstrap/hydration never see it.
 */
@Injectable({ providedIn: 'root' })
export class PreviewSessionService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly viewport = inject(ViewportService);

  /** Exchange an opaque preview link for a short-lived scoped JWT. */
  exchange(token: string): Observable<PreviewSessionResponse> {
    return this.http
      .post<PreviewSessionResponse>(
        `${environment.apiUrl}/preview/session/${encodeURIComponent(token)}`,
        {},
      )
      .pipe(tap((res) => this.store(res.jwt)));
  }

  /**
   * True only when a valid preview token is present AND there is no real
   * session. The real session always wins.
   */
  isActive(): boolean {
    if (this.auth.getToken()) {
      // A real session has taken over: drop any stale preview token so it can
      // never interfere with authenticated requests.
      this.clear();
      return false;
    }

    const token = this.read();
    if (!token) {
      return false;
    }

    const payload = this.decode(token);
    if (!payload || payload.scope !== PREVIEW_SCOPE || this.isExpired(payload)) {
      this.clear();
      return false;
    }
    return true;
  }

  getToken(): string | null {
    return this.isActive() ? this.read() : null;
  }

  getTaskId(): string | null {
    const token = this.read();
    return token ? (this.decode(token)?.previewTaskId ?? null) : null;
  }

  /** Interpose the real login, returning afterwards to the previewed task. */
  requireLogin(): void {
    const returnUrl = this.targetUrl();
    rememberRedirectIntent(returnUrl);
    void this.router.navigate([this.loginPath()], {
      queryParams: { returnUrl },
    });
  }

  /** UrlTree variant for use inside guards. */
  loginUrlTree(returnUrl: string): UrlTree {
    rememberRedirectIntent(returnUrl);
    return this.router.createUrlTree([this.loginPath()], {
      queryParams: { returnUrl },
    });
  }

  clear(): void {
    try {
      window.sessionStorage?.removeItem(PREVIEW_TOKEN_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private store(jwt: string): void {
    try {
      window.sessionStorage?.setItem(PREVIEW_TOKEN_STORAGE_KEY, jwt);
    } catch {
      // ignore
    }
  }

  private read(): string | null {
    try {
      return window.sessionStorage?.getItem(PREVIEW_TOKEN_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private loginPath(): string {
    return this.viewport.isMobile() ? '/login' : '/';
  }

  private targetUrl(): string {
    const id = this.getTaskId();
    return id ? `/tasks/do/${id}` : '/';
  }

  private isExpired(payload: PreviewJwtPayload): boolean {
    if (typeof payload.exp !== 'number') {
      return false;
    }
    return Date.now() >= payload.exp * 1000;
  }

  private decode(token: string): PreviewJwtPayload | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    try {
      const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        '=',
      );
      return JSON.parse(atob(padded)) as PreviewJwtPayload;
    } catch {
      return null;
    }
  }
}
